import { ArrowRight, FileWarning } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import CurrencyText from "../../../components/fragment/cur-text/CurrencyText";
import TabLink from "../../../components/fragment/tab-link/TabLink";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import { RazorNumberFormat } from "../../../components/razor-reuse";
import { getPromotionDetail } from "../../../redux/promotion";
import RootState, {
  PromotionMinTransactionAmount,
  PromotionRedemption,
  PromotionRewardConfig,
} from "../../../redux/type";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import { formatTime } from "../../../utils/Helper";
import {
  AUDIENCE_OPTIONS,
  isPercentType,
  PROMOTION_TYPE_LABELS,
  REGIONS_Code,
} from "./campaignForm";
import CampaignStatusBadge from "./components/CampaignStatusBadge";

export const VIEW_PROMOTION_ID_KEY = "view_promotion_id";

const REGION_LABELS: Record<string, string> = Object.fromEntries(
  REGIONS_Code.map((region) => [region.value, region.label]),
);

const AUDIENCE_LABELS: Record<string, string> = Object.fromEntries(
  AUDIENCE_OPTIONS.map((option) => [option.value, option.label]),
);

export default function PromotionDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { promotion_detail: promotion, loading_promotion_detail } =
    useSelector((state: RootState) => state.promotion);

  const [promotionId] = useState(() =>
    localStorage.getItem(VIEW_PROMOTION_ID_KEY),
  );

  const goToPromotions = () => {
    navigate(ALL_ROUTES?.DASHBOARD_REFERRAL, {
      state: { activeTab: "Promotions" },
    });
  };

  useEffect(() => {
    if (!promotionId) {
      goToPromotions();
      return;
    }
    dispatch(getPromotionDetail(promotionId) as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabLinkList = [
    { label: "Overview", active: false },
    { label: "Referral Settings", active: false },
    { label: "Promotions", active: true },
    { label: "Influencer Programme", active: false },
  ];

  const isInitialLoad = loading_promotion_detail && !promotion;

  if (isInitialLoad) {
    return (
      <DashboardLayoutMain
        title="Referral Program"
        back
        onBack={goToPromotions}
      >
        <div className="mx-[2rem] py-[40px] space-y-[16px]">
          <Skeleton height={120} />
          <Skeleton height={200} />
          <Skeleton height={280} />
        </div>
      </DashboardLayoutMain>
    );
  }

  if (!promotion) return null;

  const minTransactionAmounts = (promotion.min_transaction_amount || []).filter(
    (row) => row?.currency_code,
  );
  const rewardConfigs = (promotion.rewards || []).filter(
    (row) => row?.currency_code,
  );
  const eligibleRegions = promotion.eligible_regions || [];
  const eligibleCorridors = promotion.eligible_corridors || [];
  const redemptions = (promotion.redemptions || []).filter(
    (redemption) => !!redemption,
  );
  const totalRedemptions = promotion.times_redeemed ?? redemptions.length;

  return (
    <DashboardLayoutMain title="Referral Program" back onBack={goToPromotions}>
      <section className="bg-white mx-[2rem] pt-[10px] space-y-4 mt-[15px]">
        <TabLink
          onClick={(param) => {
            if (param?.label === "Promotions") {
              goToPromotions();
            } else {
              navigate(ALL_ROUTES?.DASHBOARD_REFERRAL, {
                state: { activeTab: param?.label },
              });
            }
          }}
          list={tabLinkList}
          className="single-customer-tab-link"
        />
      </section>

      <div className="mx-[2rem] py-[16px] space-y-[20px] max-w-[900px]">
        <section>
          <div className="flex items-center gap-[10px]">
            <p className="text-[20px] font-bold text-[#1E1E1E]">
              {promotion.name}
            </p>
            <CampaignStatusBadge status={promotion.status} />
          </div>
          {promotion.description && (
            <p className="text-[14px] text-[#758089] mt-[4px]">
              {promotion.description}
            </p>
          )}
        </section>

        {/* campaign information */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">
            Campaign Information
          </p>
          <div className="mt-[16px] grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
            <DetailField
              label="Type"
              value={PROMOTION_TYPE_LABELS[promotion.type] || promotion.type}
            />
            <DetailField
              label="Audience"
              value={AUDIENCE_LABELS[promotion.audience] || promotion.audience}
            />
            <DetailField
              label="Start Date"
              value={promotion.starts_at ? formatTime(promotion.starts_at) : "—"}
            />
            <DetailField
              label="End Date"
              value={
                promotion.ends_at ? formatTime(promotion.ends_at) : "No end date"
              }
            />
            <DetailField
              label="Usage Limit (Total)"
              value={
                promotion.usage_limit_total != null
                  ? String(promotion.usage_limit_total)
                  : "Unlimited"
              }
            />
            <DetailField
              label="Usage Limit (Per User)"
              value={
                promotion.usage_limit_per_user != null
                  ? String(promotion.usage_limit_per_user)
                  : "Unlimited"
              }
            />
            <DetailField
              label="Created By"
              value={
                promotion.created_by
                  ? `${promotion.created_by.first_name || ""} ${
                      promotion.created_by.last_name || ""
                    }`.trim() ||
                    promotion.created_by.email ||
                    "—"
                  : "—"
              }
            />
            <DetailField
              label="Created At"
              value={promotion.created_at ? formatTime(promotion.created_at) : "—"}
            />
          </div>
        </section>

        {/* eligibility */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">Eligibility</p>

          <p className="mt-[16px] text-[13px] font-medium text-[#758089]">
            Eligible Regions
          </p>
          {eligibleRegions.length > 0 ? (
            <div className="mt-[8px] flex flex-wrap gap-[8px]">
              {eligibleRegions.map((region) => (
                <span
                  key={region}
                  className="rounded-full border border-[#E4E4E7] px-[10px] py-[3px] text-[12px] text-[#3F3F46]"
                >
                  {REGION_LABELS[region] || region}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-[8px] text-[13px] text-[#A1A1AA]">
              All regions eligible
            </p>
          )}

          <p className="mt-[16px] text-[13px] font-medium text-[#758089]">
            Eligible Corridors
          </p>
          {eligibleCorridors.length > 0 ? (
            <div className="mt-[8px] flex flex-wrap gap-[8px]">
              {eligibleCorridors.map((corridor, idx) => (
                <span
                  key={`${corridor?.source}-${corridor?.destination}-${idx}`}
                  className="inline-flex items-center gap-[4px] rounded-full border border-[#734792] px-[10px] py-[3px] text-[12px] text-[#734792]"
                >
                  {corridor?.source || "—"}
                  <ArrowRight className="size-[10px]" />
                  {corridor?.destination || "—"}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-[8px] text-[13px] text-[#A1A1AA]">
              All corridors eligible
            </p>
          )}
        </section>

        {/* minimum transaction amounts */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">
            Minimum Transaction Amounts
          </p>
          <CurrencyValueList
            rows={minTransactionAmounts}
            emptyLabel="No minimum transaction amount set"
          />
        </section>

        {/* reward configuration */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <p className="text-[16px] font-bold text-[#1E1E1E]">
            Reward Configuration
          </p>
          <CurrencyValueList
            rows={rewardConfigs}
            emptyLabel="No reward configuration set"
            isPercent={isPercentType(promotion.type)}
          />
        </section>

        {/* redeemed users */}
        <section className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
          <div className="flex items-center justify-between">
            <p className="text-[16px] font-bold text-[#1E1E1E]">
              Redeemed Users
            </p>
            <span className="text-[13px] text-[#758089]">
              {totalRedemptions} redemption{totalRedemptions === 1 ? "" : "s"}
            </span>
          </div>

          {redemptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-[48px]">
              <FileWarning className="size-[32px] text-[#D4D4D8]" />
              <p className="mt-[10px] text-[13px] text-[#A1A1AA]">
                No redemptions yet
              </p>
            </div>
          ) : (
            <div className="mt-[16px] overflow-x-auto">
              <div className="min-w-[560px]">
                <div className="grid grid-cols-[1fr_100px_120px_170px] gap-[16px] px-[16px] pb-[8px] text-[11px] font-bold uppercase tracking-wide text-[#A1A1AA]">
                  <span>User</span>
                  <span>Currency</span>
                  <span>Discount</span>
                  <span>Redeemed At</span>
                </div>
                <div className="max-h-[420px] space-y-[8px] overflow-y-auto">
                  {redemptions.map((redemption, idx) => (
                    <RedemptionRow
                      key={redemption?.id || idx}
                      redemption={redemption}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </DashboardLayoutMain>
  );
}

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <p className="text-[13px] text-[#758089]">{label}</p>
      <p className="mt-[4px] text-[14px] font-medium text-[#1E1E1E]">
        {value ?? "—"}
      </p>
    </div>
  );
}

function CurrencyValueList({
  rows,
  emptyLabel,
  isPercent,
}: {
  rows: (PromotionMinTransactionAmount | PromotionRewardConfig)[];
  emptyLabel: string;
  isPercent?: boolean;
}) {
  if (rows.length === 0) {
    return <p className="mt-[12px] text-[13px] text-[#A1A1AA]">{emptyLabel}</p>;
  }
  return (
    <div className="mt-[12px] space-y-[8px]">
      {rows.map((row) => (
        <div
          key={row.currency_code}
          className="flex items-center justify-between rounded-[8px] bg-[#F4F4F5] px-[16px] py-[10px]"
        >
          <CurrencyText currency={row.currency_code} imgSize="20px" />
          <span className="text-[14px] font-medium text-[#1E1E1E]">
            {isPercent
              ? `${row?.value ?? 0}%`
              : RazorNumberFormat(row?.value, { symbol: row.currency_code })}
          </span>
        </div>
      ))}
    </div>
  );
}

function RedemptionRow({
  redemption,
}: {
  redemption: PromotionRedemption | null | undefined;
}) {
  if (!redemption) return null;

  const userName = redemption.user
    ? `${redemption.user.first_name || ""} ${
        redemption.user.last_name || ""
      }`.trim() ||
      redemption.user.email ||
      "—"
    : "—";

  return (
    <div className="grid grid-cols-[1fr_100px_120px_170px] items-center gap-[16px] rounded-[8px] bg-[#F4F4F5] px-[16px] py-[10px]">
      <div className="min-w-0">
        <p className="truncate text-[13px] font-medium text-[#1E1E1E]">
          {userName}
        </p>
        {redemption.user?.email && (
          <p className="truncate text-[12px] text-[#758089]">
            {redemption.user.email}
          </p>
        )}
      </div>
      <span className="text-[13px] text-[#3F3F46]">
        {redemption.currency_code || "—"}
      </span>
      <span className="text-[13px] text-[#3F3F46]">
        {redemption.discount_amount != null
          ? RazorNumberFormat(redemption.discount_amount, {
              symbol: redemption.currency_code || undefined,
            })
          : "—"}
      </span>
      <span className="text-[13px] text-[#758089]">
        {redemption.redeemed_at ? formatTime(redemption.redeemed_at) : "—"}
      </span>
    </div>
  );
}
